"use client";

import React, { useState } from "react";
import { MapPin, Mail, Phone, ArrowRight, ArrowLeft, CircleCheck, Loader, Github, Twitter, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const socialLinks = [{
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Linkedin,
  href: "#",
  label: "LinkedIn"
}, {
  icon: Github,
  href: "#",
  label: "GitHub"
}];
const budgetOptions = ["Quarterly Ad Spot", "Semi-Annual Campaign", "Annual Partnership", "Custom EDDM Project"];
function CollaborationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const nextStep = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(2);
  };
  const prevStep = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(1);
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-secondary">Reservation Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" className="mt-4 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  const inputStyles = "border-0 border-b border-border rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary bg-transparent dark:bg-transparent transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="flex items-center gap-4 mb-8">
        <div className={`h-1 flex-1 rounded-full transition-colors duration-300 ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
        <div className={`h-1 flex-1 rounded-full transition-colors duration-300 ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
      </div>

      <div className={`space-y-6 transition-all duration-500 ${step === 1 ? 'block opacity-100' : 'hidden opacity-0'}`}>
        <div className="space-y-2 mb-6">
          <h3 className="text-xl font-default font-semibold text-secondary">Phase 1: Business Details</h3>
          <p className="text-sm text-muted-foreground">Tell us about your local business and industry.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="name" rules={{
          required: "Business name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Business Name *</FormLabel>
                <FormControl>
                  <Input placeholder="The Local Boutique" className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Contact Email *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="owner@business.com" className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Industry Category</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Real Estate, Dentistry" className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[0-9+\-\s()]*$/,
            message: "Invalid phone format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(770) 555-0123" className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="flex justify-end pt-4">
          <Button type="button" onClick={nextStep} className="group bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm shadow-md">
            Next Step
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>

      <div className={`space-y-6 transition-all duration-500 ${step === 2 ? 'block opacity-100' : 'hidden opacity-0'}`}>
        <div className="space-y-2 mb-6">
          <h3 className="text-xl font-default font-semibold text-secondary">Phase 2: Campaign Goals</h3>
          <p className="text-sm text-muted-foreground">Help us understand your target audience in Metro Atlanta.</p>
        </div>

        <FormField control={control} name="project_details" rules={{
        required: "Campaign goals are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Marketing Goals *</FormLabel>
              <FormControl>
                <Textarea placeholder="What would you like to achieve with this mailing? (e.g. New leads, brand awareness)" className={`min-h-[120px] resize-none ${inputStyles}`} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="budget" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Preferred Package</FormLabel>
                <FormControl>
                  <select {...field} className={`w-full h-9 bg-transparent outline-none text-sm ${inputStyles} appearance-none cursor-pointer`}>
                    <option value="" disabled className="text-muted-foreground dark:bg-background">Select a package...</option>
                    {budgetOptions.map((option, i) => <option key={i} value={option} data-index={i} className="dark:bg-background text-foreground">
                        {option}
                      </option>)}
                  </select>
                </FormControl>
                <FormDescription className="font-sans">Choose the frequency that fits your growth plan.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="timeline" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-default">Target Launch Date</FormLabel>
                <FormControl>
                  <Input type="date" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="font-sans">When should your postcards hit mailboxes?</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
            <p className="text-sm text-destructive font-medium">{submitError}</p>
          </div>}

        <div className="flex justify-between pt-4">
          <Button type="button" variant="ghost" onClick={prevStep} className="group text-secondary hover:bg-secondary/5">
            <ArrowLeft className="mr-2 size-4 transition-transform group-hover:-translate-x-1" />
            Back
          </Button>
          <Button type="submit" disabled={isSubmitting} className="min-w-[160px] bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm shadow-md">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Processing...
              </> : 'Claim Your Category'}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function LetsCollaborate() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="238034" id="claim-your-category" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      <Toaster position="bottom-right" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#222f4708_1px,transparent_1px),linear-gradient(to_bottom,#222f4708_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {}
          <div className={`lg:col-span-5 space-y-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tight text-secondary mb-4">
                Claim Your Category
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                Exclusivity is our promise. We only feature one business per industry on our premium mailers. Secure your spot before your competitors do.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-sm bg-primary/10 text-secondary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-secondary">Service Area</h4>
                  <p className="text-muted-foreground mt-1 font-sans">Johns Creek & Metro Atlanta<br />Georgia, USA</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-sm bg-primary/10 text-secondary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-secondary">Direct Inquiry</h4>
                  <p className="text-muted-foreground mt-1 font-sans">
                    <Link to="/ContactPage" className="hover:text-primary transition-colors text-secondary">
                      hello@peachtreepost.com
                    </Link>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-sm bg-primary/10 text-secondary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-secondary">Call Our Office</h4>
                  <p className="text-muted-foreground mt-1 font-sans">
                    <Link to="#" className="hover:text-primary transition-colors text-secondary">
                      (770) 555-POST
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <Separator className="bg-border" />

            <div className="space-y-4">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground font-default">Connect With Us</h4>
              <div className="flex gap-4">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={i} to={social.href} data-index={i} className="p-2 rounded-sm bg-muted text-secondary hover:bg-primary hover:text-primary-foreground transition-colors" aria-label={social.label}>
                      <Icon className="size-5" />
                    </Link>;
              })}
              </div>
            </div>

            {}
            <div className="relative aspect-[4/3] w-full rounded-sm overflow-hidden border border-border bg-muted group">
              <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/atlanta_neighborhood_map.png)] bg-cover bg-center opacity-60 grayscale group-hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center gap-2 bg-background/95 backdrop-blur-sm px-3 py-1.5 rounded-sm border border-border shadow-sm">
                  <div className="size-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs font-mono font-medium text-secondary">JOHNS_CREEK_ACTIVE</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Card className="border-border shadow-4xl shadow-secondary/5 bg-card/80 backdrop-blur-xl h-full rounded-sm">
              <CardContent className="p-8 md:p-10">
                <FormProvider formKey="claim_category_contact" formId="claim_category_contact" sectionName="lets-collaborate" defaultValues={{
                name: "",
                email: "",
                company: "",
                phone: "",
                project_details: "",
                budget: "",
                timeline: ""
              }} submitText="Claim Your Category" successMessage="Your inquiry has been received. Our team will verify your category availability and contact you within 24 hours.">
                  <CollaborationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}